import React, { useEffect, useState } from "react";
import { Brain, Save } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";

export default function CognitivePage() {
  const { t } = useLanguage();
  const [profile, setProfile] = useState(null);
  const [dims, setDims] = useState({});
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/cognitive/profile"), api.get("/cognitive/dimensions")])
      .then(([p, d]) => {
        setProfile(p.data);
        setDraft(p.data?.profile || {});
        setDims(d.data || {});
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  async function save() {
    setBusy(true);
    setError("");
    try {
      const updates = {};
      Object.keys(dims).forEach((k) => {
        if (draft[k] && draft[k] !== profile?.profile?.[k]) updates[k] = draft[k];
      });
      if (Object.keys(updates).length === 0) {
        setBusy(false);
        return;
      }
      const { data } = await api.patch("/cognitive/profile", { updates });
      setProfile(data);
      setDraft(data?.profile || {});
      setSavedAt(new Date().toISOString());
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="cognitive-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          {t.cognitive.eyebrow}
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">{t.cognitive.title}</h1>
        <p className="mt-1 text-sm text-slate-500">{t.cognitive.sub}</p>
      </header>

      {error && (
        <div className="mios-card" data-testid="cognitive-error">
          <div className="mios-card-body text-sm text-red-700">{error}</div>
        </div>
      )}

      <div className="mios-card" data-testid="cognitive-notice">
        <div className="mios-card-body text-sm text-slate-700">
          <span className="font-semibold text-slate-900">{t.cognitive.noticeTitle}: </span>
          {t.cognitive.notice}
        </div>
      </div>

      <div className="mios-card" data-testid="cognitive-profile-card">
        <div className="mios-card-header">
          <div className="mios-card-title flex items-center gap-2">
            <Brain className="h-4 w-4 text-slate-400" />
            {t.cognitive.dimensions}
          </div>
          {savedAt && (
            <span className="text-[11px] text-emerald-600">{t.cognitive.savedHint}</span>
          )}
        </div>
        <div className="mios-card-body grid gap-3 sm:grid-cols-2">
          {Object.keys(dims).map((k) => (
            <div key={k} className="rounded-lg border border-slate-200 p-3" data-testid={`cog-dim-${k}`}>
              <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">
                {t.cognitive.fields[k] || k}
              </div>
              <select
                className="mios-input mt-2"
                value={draft[k] || ""}
                onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                data-testid={`cog-select-${k}`}
              >
                {(dims[k] || []).map((opt) => (
                  <option key={opt} value={opt}>
                    {(t.cognitive.values && t.cognitive.values[opt]) || opt}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={save}
            disabled={busy}
            data-testid="cognitive-save-btn"
            className="mios-btn mios-btn-primary"
          >
            <Save className="h-4 w-4" />
            {busy ? t.common.loading : t.cognitive.save}
          </button>
        </div>
      </div>

      <div className="mios-card" data-testid="cognitive-meta">
        <div className="mios-card-body grid gap-2 text-sm text-slate-600 sm:grid-cols-3">
          <Meta label={t.cognitive.confidence} value={profile?.profile?.confidence?.toFixed?.(2) ?? "0.00"} />
          <Meta label={t.cognitive.samples} value={profile?.profile?.samples ?? 0} />
          <Meta
            label={t.cognitive.updatedAt}
            value={profile?.updated_at ? new Date(profile.updated_at).toLocaleString() : "—"}
          />
        </div>
      </div>
    </div>
  );
}

function Meta({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
      <div className="mt-1 mono text-base font-medium text-slate-900">{value}</div>
    </div>
  );
}
